/* eslint-disable no-use-before-define */
import { schema } from 'normalizr';
import { createAction } from 'redux-api-middleware';
import { isEmpty, isNil } from 'lodash';
import { downloadOrOpenBlob } from '../utils/download';
import { handleResponse, handleError, buildQueryString } from '../utils';
import { getToken } from '../utils/auth';

export const statementSchema = new schema.Entity(
  'statements',
  {},
  {
    idAttribute: '_id',
  },
);
export const statementActivitySchema = new schema.Entity(
  'statementActivity',
  {},
  {
    idAttribute: '_id',
  },
);

export const statementDetailSchema = new schema.Entity(
  'statementDetail',
  {},
  {
    idAttribute: '_id',
    processStrategy(statement) {
      parseCommentsAndAdjustments(statement);
      return statement;
    },
  },
);

export const FETCH_ATTACHMENT_REQUEST = 'FETCH_ATTACHMENT_REQUEST';
export const FETCH_ATTACHMENT_SUCCESS = 'FETCH_ATTACHMENT_SUCCESS';
export const FETCH_ATTACHMENT_FAILURE = 'FETCH_ATTACHMENT_FAILURE';
export const FETCH_PAYGO_ATTACHMENT_REQUEST = 'FETCH_PAYGO_ATTACHMENT_REQUEST';
export const FETCH_PAYGO_ATTACHMENT_SUCCESS = 'FETCH_PAYGO_ATTACHMENT_SUCCESS';
export const FETCH_PAYGO_ATTACHMENT_FAILURE = 'FETCH_PAYGO_ATTACHMENT_FAILURE';
export const FETCH_STATEMENTS_REQUEST = 'FETCH_STATEMENTS_REQUEST';
export const FETCH_STATEMENTS_SUCCESS = 'FETCH_STATEMENTS_SUCCESS';
export const FETCH_STATEMENTS_FAILURE = 'FETCH_STATEMENTS_FAILURE';
export const FETCH_STATEMENTDETAIL_REQUEST = 'FETCH_STATEMENTDETAIL_REQUEST';
export const FETCH_STATEMENTDETAIL_SUCCESS = 'FETCH_STATEMENTDETAIL_SUCCESS';
export const FETCH_STATEMENTDETAIL_FAILURE = 'FETCH_STATEMENTDETAIL_FAILURE';
export const FETCH_STATEMENTPERCAP_REQUEST = 'FETCH_STATEMENTPERCAP_REQUEST';
export const FETCH_STATEMENTPERCAP_SUCCESS = 'FETCH_STATEMENTPERCAP_SUCCESS';
export const FETCH_STATEMENTPERCAP_FAILURE = 'FETCH_STATEMENTPERCAP_FAILURE';
export const FETCH_STATEMENTACTIVITY_REQUEST = 'FETCH_STATEMENTACTIVITY_REQUEST';
export const FETCH_STATEMENTACTIVITY_SUCCESS = 'FETCH_STATEMENTACTIVITY_SUCCESS';
export const FETCH_STATEMENTACTIVITY_FAILURE = 'FETCH_STATEMENTACTIVITY_FAILURE';
export const DELETE_STATEMENTATTACHMENT_REQUEST = 'DELETE_STATEMENTATTACHMENT_REQUEST';
export const DELETE_STATEMENTATTACHMENT_SUCCESS = 'DELETE_STATEMENTATTACHMENT_SUCCESS';
export const DELETE_STATEMENTATTACHMENT_FAILURE = 'DELETE_STATEMENTATTACHMENT_FAILURE';
export const REMOVE_STATEMENTATTACHMENT = 'REMOVE_STATEMENTATTACHMENT';
export const APPROVAL_STATEMENT_REQUEST = 'APPROVAL_STATEMENT_REQUEST';
export const APPROVAL_STATEMENT_SUCCESS = 'APPROVAL_STATEMENT_SUCCESS';
export const APPROVAL_STATEMENT_FAILURE = 'APPROVAL_STATEMENT_FAILURE';

export const REGENERATE_STATEMENT_REQUEST = 'REGENERATE_STATEMENT_REQUEST';
export const REGENERATE_STATEMENT_SUCCESS = 'REGENERATE_STATEMENT_SUCCESS';
export const REGENERATE_STATEMENT_FAILURE = 'REGENERATE_STATEMENT_FAILURE';

export const UPLOAD_STATEMENTATTACHMENT_REQUEST = 'UPLOAD_STATEMENTATTACHMENT_REQUEST';
export const UPLOAD_STATEMENTATTACHMENT_SUCCESS = 'UPLOAD_STATEMENTATTACHMENT_SUCCESS';
export const UPLOAD_STATEMENTATTACHMENT_FAILURE = 'UPLOAD_STATEMENTATTACHMENT_FAILURE';

function parseCommentsAndAdjustments(statement) {
  const { comments, paymentBreakdown } = statement;

  if (
    isNil(paymentBreakdown) ||
    isNil(comments) ||
    isEmpty(paymentBreakdown) ||
    isEmpty(comments)
  ) {
    return;
  }

  const royaltiesEarnedAdjComments = statement.comments.filter(
    x => x.type === 'AdjustmentToRoyaltiesEarned',
  );
  royaltiesEarnedAdjComments.forEach((comment, index) => {
    // eslint-disable-next-line no-param-reassign
    comment.id = index + 1;
  });

  paymentBreakdown.forEach(payment => {
    if (
      payment.title.toLowerCase() !== 'earning adjustments' ||
      isNil(payment.details) ||
      isEmpty(payment.details)
    ) {
      return;
    }

    payment.details.forEach(detail => {
      const comment = royaltiesEarnedAdjComments.find(
        c => c.adjustmentId === detail.adjustmentId.toString(),
      );

      if (!comment) {
        return;
      }

      // eslint-disable-next-line no-param-reassign
      detail.commentId = comment.id;
    });
  });
}

export const fetchStatements = filters => {
  return createAction({
    endpoint: `${window.config.sapiUrl}/statements?${buildQueryString(filters)}`,
    method: 'GET',
    types: [
      { type: FETCH_STATEMENTS_REQUEST },
      {
        type: FETCH_STATEMENTS_SUCCESS,
        payload: (action, state, res) => handleResponse([statementSchema], action, state, res),
        meta: (action, state, res) => ({
          totalCount: parseInt(res.headers.get('Total-Count'), 10),
        }),
      },
      { type: FETCH_STATEMENTS_FAILURE, payload: handleError },
    ],
  });
};

/* Don't change this back to using existing statementSchema, statementDetailsSchema is needed 
because api response includes a more comprehensive list of things not returned from fetch statements.
*/
export const fetchStatementDetails = (partnerAddress, statementId) =>
  createAction({
    endpoint: `${window.config.sapiUrl}/partner/${partnerAddress}/statements/${statementId}`,
    method: 'GET',
    types: [
      { type: FETCH_STATEMENTDETAIL_REQUEST },
      {
        type: FETCH_STATEMENTDETAIL_SUCCESS,
        payload: (action, state, res) => handleResponse(statementDetailSchema, action, state, res),
      },
      { type: FETCH_STATEMENTDETAIL_FAILURE, payload: handleError },
    ],
  });

export const deleteStatementAttachment = (statementId, partnerAddress, documentName) =>
  createAction({
    endpoint: `${window.config.sapiUrl}/partner/${partnerAddress}/statements/${statementId}/attachment`,
    method: 'DELETE',
    body: JSON.stringify({ documentName }),
    types: [
      { type: DELETE_STATEMENTATTACHMENT_REQUEST },
      {
        type: DELETE_STATEMENTATTACHMENT_SUCCESS,
      },
      { type: DELETE_STATEMENTATTACHMENT_FAILURE, payload: handleError },
    ],
  });

export const uploadStatementAttachment = (statementId, partnerAddress, attachments) => {
  const formData = new FormData();
  formData.append('file', attachments.files[0]);
  formData.append('documentType', attachments.type);

  return createAction({
    endpoint: `${window.config.sapiUrl}/partner/${partnerAddress}/statements/${statementId}/attachment`,
    method: 'PATCH',
    body: formData,
    options: {
      isFileUpload: true,
    },
    types: [
      { type: UPLOAD_STATEMENTATTACHMENT_REQUEST },
      {
        type: UPLOAD_STATEMENTATTACHMENT_SUCCESS,
        meta: (action, state, res) => ({ flashMessage: res.message }),
      },
      { type: UPLOAD_STATEMENTATTACHMENT_FAILURE, payload: handleError },
    ],
  });
};

export const fetchStatementActivity = statementId =>
  createAction({
    endpoint: `${window.config.apiUrl}/workFlows?area=statements&resourceId=${statementId}&latest=true`,
    method: 'GET',
    types: [
      { type: FETCH_STATEMENTACTIVITY_REQUEST },
      {
        type: FETCH_STATEMENTACTIVITY_SUCCESS,
        payload: (action, state, res) =>
          handleResponse(statementActivitySchema, action, state, res),
      },
      { type: FETCH_STATEMENTACTIVITY_FAILURE, payload: handleError },
    ],
  });

/**
 * Fetch partner attachment blob.
 *
 * @param {string} partnerAddress - Eth address of partner.
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const fetchAttachment = (url, open) =>
  createAction({
    endpoint: url,
    method: 'GET',
    headers: {
      Authorization: `Bearer ${getToken()}`,
      'Content-Type': 'application/octet-stream',
    },
    types: [
      { type: FETCH_ATTACHMENT_REQUEST },
      {
        type: FETCH_ATTACHMENT_SUCCESS,
        payload: async (action, state, res) => {
          const blob = await res.blob();
          const fileName = res.headers.get('filename');
          downloadOrOpenBlob(blob, fileName, open);
        },
      },
      { type: FETCH_ATTACHMENT_FAILURE },
    ],
  });

/**
 * Fetch partner payGo attachment blob.
 *
 * @param {string} partnerAddress - Eth address of partner.
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const fetchPayGoAttachment = (url, open) =>
  createAction({
    endpoint: url,
    method: 'GET',
    headers: {
      Authorization: `Bearer ${getToken()}`,
      'Content-Type': 'application/octet-stream',
    },
    types: [
      { type: FETCH_PAYGO_ATTACHMENT_REQUEST },
      {
        type: FETCH_PAYGO_ATTACHMENT_SUCCESS,
        payload: async (action, state, res) => {
          const blob = await res.blob();
          const fileName = res.headers.get('filename');
          downloadOrOpenBlob(blob, fileName, open);
        },
      },
      { type: FETCH_PAYGO_ATTACHMENT_FAILURE },
    ],
  });

export const removeAttachment = (statementId, documentName) => {
  return {
    type: REMOVE_STATEMENTATTACHMENT,
    payload: {
      statementId,
      documentName,
    },
  };
};
/**
 * Submits a statement for approval/ rejection.
 *
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const statementApproval = (statementId, values, flashMessage) =>
  createAction({
    endpoint: `${window.config.sapiUrl}/statements/${statementId}/submit-for-workflow`,
    method: 'PATCH',
    body: JSON.stringify(values),
    types: [
      { type: APPROVAL_STATEMENT_REQUEST },
      {
        type: APPROVAL_STATEMENT_SUCCESS,
        meta: { flashMessage },
        payload: (action, state, res) => handleResponse(statementSchema, action, state, res),
      },
      {
        type: APPROVAL_STATEMENT_FAILURE,
        payload: handleError,
      },
    ],
  });

/**
 * Submits a statement for regeneration.
 *
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const statementRegeneration = (statementId, values, flashMessage) =>
  createAction({
    endpoint: `${window.config.sapiUrl}/statements/${statementId}/regenerate`,
    method: 'PATCH',
    body: JSON.stringify(values),
    types: [
      { type: REGENERATE_STATEMENT_REQUEST },
      {
        type: REGENERATE_STATEMENT_SUCCESS,
        meta: { flashMessage },
      },
      {
        type: REGENERATE_STATEMENT_FAILURE,
        payload: handleError,
      },
    ],
  });
